import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil } from 'lucide-react';
import { Author } from '@/types/article';
import { useAuthorManagement } from '@/hooks/useAuthorManagement';
import { AvatarUpload } from '@/components/ui/avatar-upload';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { twitterUrlSchema, linkedInUrlSchema, websiteUrlSchema } from '@/utils/socialMediaValidation';

const authorSchema = z.object({
  display_name: z.string()
    .trim()
    .min(1, 'Author name is required')
    .max(100, 'Author name must be less than 100 characters'),
  bio: z.string()
    .trim()
    .max(500, 'Bio must be less than 500 characters')
    .optional(),
  avatar_url: z.string().optional(),
  twitter: twitterUrlSchema,
  linkedin: linkedInUrlSchema,
  website: websiteUrlSchema
});

interface AuthorSelectorProps {
  authors: Author[];
  value?: string;
  onValueChange: (value: string) => void;
  onAuthorsUpdate: () => void;
}

export const AuthorSelector: React.FC<AuthorSelectorProps> = ({
  authors,
  value,
  onValueChange,
  onAuthorsUpdate
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<Author | null>(null);
  const { createAuthor, updateAuthor, loading } = useAuthorManagement();

  const form = useForm<z.infer<typeof authorSchema>>({
    resolver: zodResolver(authorSchema),
    defaultValues: {
      display_name: '',
      bio: '',
      avatar_url: '',
      twitter: '',
      linkedin: '',
      website: ''
    }
  });

  const handleCreateAuthor = async (data: z.infer<typeof authorSchema>) => {
    let isMounted = true;
    
    try {
      const social_links = {
        twitter: data.twitter || null,
        linkedin: data.linkedin || null,
        website: data.website || null
      };
      
      const newAuthor = await createAuthor(
        data.display_name, 
        data.bio, 
        data.avatar_url,
        social_links
      );
      
      if (newAuthor && isMounted) {
        await onAuthorsUpdate();
        onValueChange(newAuthor.id);
        setIsDialogOpen(false);
        form.reset();
      }
    } catch (error) {
      console.error('Error creating author:', error);
    }
    
    return () => {
      isMounted = false;
    };
  };

  const handleEditAuthor = async (data: z.infer<typeof authorSchema>) => {
    if (!editingAuthor) return;
    
    try {
      const social_links = {
        twitter: data.twitter || null,
        linkedin: data.linkedin || null,
        website: data.website || null
      };
      
      const updated = await updateAuthor(
        editingAuthor.id, 
        data.display_name, 
        data.bio, 
        data.avatar_url,
        social_links
      );
      
      if (updated) {
        await onAuthorsUpdate();
        setIsEditDialogOpen(false);
        setEditingAuthor(null);
        form.reset();
      }
    } catch (error) {
      console.error('Error updating author:', error);
    }
  };

  const openEditDialog = (author: Author) => {
    setEditingAuthor(author);
    const socialLinks = author.social_links || {};
    form.reset({
      display_name: author.display_name,
      bio: author.bio || '',
      avatar_url: author.avatar_url || '',
      twitter: socialLinks.twitter || '',
      linkedin: socialLinks.linkedin || '',
      website: socialLinks.website || ''
    });
    setIsEditDialogOpen(true);
  };

  // Filter out any invalid authors
  const validAuthors = authors?.filter(author => 
    author?.id && 
    author?.display_name && 
    typeof author.id === 'string' &&
    typeof author.display_name === 'string'
  ) || [];

  const handleValueChange = (newValue: string) => {
    try {
      if (newValue && typeof newValue === 'string') {
        onValueChange(newValue);
      }
    } catch (error) {
      console.error('Error changing author value:', error);
    }
  };

  return (
    <div className="space-y-2">
      <Select onValueChange={handleValueChange} value={value || undefined}>
        <SelectTrigger className="bg-background">
          <SelectValue placeholder="Select author" />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border z-50">
          {validAuthors.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No authors available. Create one below.
            </div>
          ) : (
            validAuthors.map((author) => (
              <SelectItem key={author.id} value={author.id}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={author.avatar_url || ''} alt={author.display_name} />
                    <AvatarFallback className="text-xs">
                      {author.display_name.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span>{author.display_name}</span>
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {value && validAuthors.find(a => a.id === value) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const author = validAuthors.find(a => a.id === value);
            if (author) openEditDialog(author);
          }}
          className="w-full"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit Author
        </Button>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Create New Author
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Author</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleCreateAuthor)} className="space-y-4">
              <FormField
                control={form.control}
                name="display_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter author name..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bio (Optional)</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Enter author bio..." rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="avatar_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author Avatar</FormLabel>
                    <FormControl>
                      <AvatarUpload
                        currentAvatar={field.value}
                        onUpload={(url) => field.onChange(url)}
                        onRemove={() => field.onChange('')}
                        fallbackText={form.watch('display_name')?.substring(0, 2).toUpperCase() || 'AA'}
                        maxSize={2}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <h3 className="text-sm font-medium">Social Media Links (Optional)</h3>
                
                <FormField
                  control={form.control}
                  name="twitter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Twitter/X</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="@username or https://twitter.com/username" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Enter @username or full URL - will be automatically normalized
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="linkedin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>LinkedIn</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="username or https://linkedin.com/in/username" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Enter username or full URL - will be automatically normalized
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="yourwebsite.com or https://yourwebsite.com" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Enter domain or full URL - https:// will be added automatically
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Author'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Author Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Author</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleEditAuthor)} className="space-y-4">
              <FormField
                control={form.control}
                name="display_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter author name..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bio (Optional)</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Enter author bio..." rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="avatar_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Author Avatar</FormLabel>
                    <FormControl>
                      <AvatarUpload
                        currentAvatar={field.value}
                        onUpload={(url) => field.onChange(url)}
                        onRemove={() => field.onChange('')}
                        fallbackText={form.watch('display_name')?.substring(0, 2).toUpperCase() || 'AA'}
                        maxSize={2}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <h3 className="text-sm font-medium">Social Media Links (Optional)</h3>
                
                <FormField
                  control={form.control}
                  name="twitter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Twitter/X</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="@username or https://twitter.com/username" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Enter @username or full URL - will be automatically normalized
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="linkedin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>LinkedIn</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="username or https://linkedin.com/in/username" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Enter username or full URL - will be automatically normalized
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="yourwebsite.com or https://yourwebsite.com" 
                          {...field} 
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Enter domain or full URL - https:// will be added automatically
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingAuthor(null);
                }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};